"use client";

import Image from "next/image";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

/**
 * A screenshot that opens over the page when clicked — the integration
 * pages' gallery and every blog screenshot.
 *
 * The open view is as large as the screen allows and never larger than the
 * file: the shot keeps its aspect ratio and is scaled down until it fits both
 * the width and the height of the viewport, so a tall chat shot is seen whole
 * rather than scrolled. The dialog is `w-max`: a fixed box at `left: 50%`
 * shrinks to fit at most half the viewport, which clipped a wide shot.
 * Built on the app's dialog, so Escape, the overlay and the close button all
 * close it and focus returns to the shot.
 */
export function ZoomableScreenshot({ src, width, height, alt, sizes, className }: {
  readonly src: string;
  readonly width: number;
  readonly height: number;
  readonly alt: string;
  /** `sizes` for the shot in its place on the page. */
  readonly sizes: string;
  readonly className?: string;
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button type="button" className="block w-full cursor-zoom-in text-left">
          <Image src={src} width={width} height={height} alt={alt} sizes={sizes} className={className} />
        </button>
      </DialogTrigger>
      <DialogContent className="w-max max-w-none gap-0 overflow-hidden rounded-xl border-border-subtle bg-surface-elevated p-0 sm:max-w-none">
        <DialogTitle className="sr-only">{alt}</DialogTitle>
        <Image
          src={src}
          width={width}
          height={height}
          alt={alt}
          sizes="100vw"
          // The width is spelled out: left to `w-auto`, the browser derives
          // it from the srcset candidate it picked (a 1920w file for a
          // 100vw slot reads as 1.5x density), which opened a 1428px shot at
          // 952px. The smallest of the file, the viewport's width, and the
          // width at which its height fills the viewport's height. The 2px
          // are the dialog's border, inside its own 100vh - 2rem cap.
          style={{
            width: `min(${String(width)}px, calc(100vw - 2rem - 2px), calc((100dvh - 2rem - 2px) * ${String(width / height)}))`,
          }}
          className="block h-auto"
        />
      </DialogContent>
    </Dialog>
  );
}
